import { formatPrice } from '../../simple-box-wrapper-options/utils'

export const DEFAULT_IMAGE =
  'https://cdn.minicoursegenerator.com/assets/landing-page-templates/thumbnails/Default.png'
const CDN_BASE_URL = 'https://cdn.minicoursegenerator.com/'

export const ensureHttps = url => {
  if (!url) return DEFAULT_IMAGE
  if (url.startsWith('http://') || url.startsWith('https://')) return url
  return `${CDN_BASE_URL}${url.startsWith('/') ? url.substring(1) : url}`
}

export const isCollectionItem = item =>
  item.type === 1 || item.type === 'Collection'

export const COLLECTION_ICON =
  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 3 3 8l9 5 9-5-9-5Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="m3 13 9 5 9-5" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>'

export const COURSE_ICON =
  '<svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M4 5h6a2 2 0 0 1 2 2v12a2 2 0 0 0-2-2H4V5Zm16 0h-6a2 2 0 0 0-2 2v12a2 2 0 0 1 2-2h6V5Z" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>'

export const clampProgress = progress =>
  Math.max(0, Math.min(100, Math.round(progress || 0)))

// In progress takes the landing page's primary colour, set as --page-accent on the block (and on
// the "Your learning" popup, which sits on document.body); finished stays green.
export const progressColor = value =>
  value < 100 ? 'var(--page-accent, #015FFB)' : '#37D768'

export const renderProgress = progress => {
  const value = clampProgress(progress)
  const color = progressColor(value)

  return `
    <div class="section-footer">
      <div class="icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M18.125 4.37502V13.75C18.1245 13.839 18.105 13.9268 18.0678 14.0077C18.0306 14.0885 17.9766 14.1605 17.9094 14.2188C16.7156 15.2524 15.5742 15.625 14.4859 15.625C13.0086 15.625 11.6289 14.9422 10.3453 14.3086C8.27109 13.2813 6.46797 12.3914 4.375 14.0445V17.5C4.375 17.6658 4.30915 17.8247 4.19194 17.942C4.07473 18.0592 3.91576 18.125 3.75 18.125C3.58424 18.125 3.42527 18.0592 3.30806 17.942C3.19085 17.8247 3.125 17.6658 3.125 17.5V4.37502C3.1256 4.28598 3.14522 4.19809 3.18254 4.11725C3.21986 4.03641 3.27403 3.96448 3.34141 3.90627C6.15391 1.47033 8.67812 2.71799 10.9031 3.81877C13.0469 4.8797 14.9055 5.79689 17.0906 3.90627C17.1809 3.82797 17.2918 3.77722 17.4101 3.76003C17.5284 3.74283 17.6491 3.75992 17.758 3.80925C17.8669 3.85859 17.9593 3.93811 18.0243 4.03839C18.0894 4.13866 18.1243 4.25549 18.125 4.37502Z" style="fill: ${color}"/>
        </svg>
      </div>
      <div class="bar" style="width: 100%; height: 6px; background-color: #D4DADF; border-radius: 50px; overflow: hidden;">
        <div style="width: ${value}%; height: 100%; background-color: ${color}; border-radius: 5px;"></div>
      </div>
      <div class="label">${value}%</div>
    </div>
  `
}

/**
 * Cards reuse the course-list / collection-list markup so every block that shows catalog
 * content picks up the same padding, typography and image sizing instead of styling its own.
 */
export const renderCard = (item, priceTagActive) => {
  const isCollection = isCollectionItem(item)
  // Both types render as course-list cards, so the grid, image heights and column rules are
  // exactly the ones the course list block already uses.
  const prefix = 'course-list'

  const icon = isCollection ? COLLECTION_ICON : COURSE_ICON

  // With the price tag on, every card carries the price row, as in the collection list, so the
  // progress bars line up across the row. Courses have no price: theirs is an invisible
  // placeholder of the same height.
  const important =
    priceTagActive !== true
      ? ''
      : isCollection
        ? `<div class="section-important">
             <div class="section-price"><p>${formatPrice(item.price)}</p></div>
           </div>`
        : `<div class="section-important" aria-hidden="true" style="visibility: hidden">
             <div class="section-price"><p>&nbsp;</p></div>
           </div>`

  return `
    <div class="${prefix}-container-preview" data-catalog-key="${item.key}">
      <div class="${prefix}-inner-container">
        <div class="${prefix}-sections-container">
          <div class="${prefix}-section">
            <div class="${prefix}-image">
              <div>
                <img src="${
                  !item.thumbnail || item.thumbnail === ''
                    ? DEFAULT_IMAGE
                    : ensureHttps(item.thumbnail)
                }" alt="${item.name || ''}">
              </div>
            </div>
          </div>
          <div class="${prefix}-title">
            <div class="catalog-card-label">
              <span>${isCollection ? 'LEARNING PATH' : 'COURSE'}</span>
              ${icon}
            </div>
            <div class="section-text-header"><p>${item.name || 'Untitled'}</p></div>
            <div class="section-text">${item.description || ''}</div>
            ${important}
          </div>
          ${renderProgress(item.progress)}
        </div>
      </div>
    </div>
  `
}

/**
 * Placeholder cards for a block with nothing to show yet. They sit in the same course-list grid
 * as real cards, so the column setting shapes the empty block exactly as it will the full one.
 */
export const renderSkeletonCards = (count, { withProgress = false } = {}) => {
  const card = `
    <div class="block-skeleton-card" aria-hidden="true">
      <div class="block-skeleton-image"></div>
      <div class="block-skeleton-body">
        <div class="block-skeleton-lines">
          <span class="block-skeleton-line is-short"></span>
          <span class="block-skeleton-line"></span>
          <span class="block-skeleton-line is-medium"></span>
        </div>
        ${
          withProgress
            ? `<div class="block-skeleton-progress">
                 <span class="block-skeleton-dot"></span>
                 <span class="block-skeleton-line"></span>
               </div>`
            : ''
        }
      </div>
    </div>
  `

  return `<div class="course-list-wrapper">${card.repeat(count)}</div>`
}
